import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SdTestAppBenchClient, type BenchCommandRegistryEntry } from "@/lib/sdtestapp/sdTestAppBenchClient";

export function BenchCommandDocsTab({
  port,
  machineId,
  connectorId,
  canActuate,
  observeOnly = false,
}: {
  port: string;
  machineId?: string;
  connectorId: string | null;
  canActuate: boolean;
  observeOnly?: boolean;
}): JSX.Element {
  const client = useMemo(() => new SdTestAppBenchClient(port), [port]);
  const [commands, setCommands] = useState<BenchCommandRegistryEntry[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [argsJson, setArgsJson] = useState("{}");
  const [resultJson, setResultJson] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const rows =
          observeOnly && machineId
            ? await SdTestAppBenchClient.relayCommandRegistry(machineId)
            : await client.fetchCommandRegistry();
        setCommands(rows);
        if (rows[0]) setSelectedKey(rows[0].commandKey);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      }
    };
    if (observeOnly && machineId) {
      void load();
      return undefined;
    }
    if (!canActuate) return undefined;
    void load();
    return undefined;
  }, [canActuate, client, machineId, observeOnly]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Browse and execute registered M2M/ASCII commands against the selected device, mirroring the NiceGUI Command Docs tab.
      </p>
      {observeOnly ? (
        <p className="text-sm text-muted-foreground">Command registry is read-only via relay observe. Execute commands locally on the bench PC.</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium" htmlFor="bench-command-key">
            Command
          </label>
          <select
            id="bench-command-key"
            className="mt-1 block w-full rounded border px-2 py-1 text-sm"
            value={selectedKey}
            onChange={(event) => setSelectedKey(event.target.value)}
          >
            {commands.map((command) => (
              <option key={command.commandKey} value={command.commandKey}>
                {command.label} ({command.commandKey})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium" htmlFor="bench-command-args">
            Args JSON
          </label>
          <Input id="bench-command-args" className="mt-1 font-mono text-xs" value={argsJson} onChange={(event) => setArgsJson(event.target.value)} />
        </div>
      </div>
      {!observeOnly ? (
      <Button
        type="button"
        disabled={!canActuate || !connectorId || !selectedKey || pending}
        onClick={() => {
          void (async () => {
            if (!connectorId) return;
            setPending(true);
            setError(null);
            try {
              const args = argsJson.trim() === "" ? {} : (JSON.parse(argsJson) as Record<string, unknown>);
              const result = await client.executeCommandDoc({
                connectorId,
                commandKey: selectedKey,
                args,
              });
              setResultJson(JSON.stringify(result, null, 2));
            } catch (actionError) {
              setError(actionError instanceof Error ? actionError.message : String(actionError));
            } finally {
              setPending(false);
            }
          })();
        }}
      >
        Execute command
      </Button>
      ) : null}
      {pending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Running command…
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {resultJson ? <pre className="max-h-96 overflow-auto rounded border bg-muted/30 p-3 text-xs">{resultJson}</pre> : null}
    </div>
  );
}
